'use client';

import { EntitlementsProvider } from '@contexts/EntitlementsContext';
import { AtomicContext } from '@contexts/coveo/AtomicContext';
import { AtomicLayoutSection } from '@coveo/atomic-react';
import { Tag } from '@lib/types/common/Tag';
import { useContext } from 'react';
import { HeadlessProductCardWrapper } from './HeadlessProductCardWrapper';
import { useResults } from './hooks/useResults';

type AtomicResultsSectionProps = {
  productTags?: Tag[];
  context: AtomicContext;
};

/**
 * Results Section with fluid responsive grid based on Figma specs:
 * Grid automatically adjusts columns based on card min-width and available space.
 * Cards wrap naturally without fixed column counts, progressively scaling from 1 to 6+ columns.
 *
 * Mobile-first card min-widths:
 * - Base (<430px): 18rem (288px) - 1 column on small phones
 * - 430px+: 12rem (192px) - 2 columns on larger phones
 * - sm (640px+): 12rem (192px) - 3 columns on tablets
 * - lg (1024px+): 14rem (224px) - 4 columns on desktop
 * - 2xl (1536px+): 13rem (208px) - 6+ columns on wide/ultra-wide displays
 */
export const AtomicResultsSection = ({
  productTags,
  context,
}: AtomicResultsSectionProps) => {
  const { searchEngine } = useContext(context);
  const { results: resultState } = useResults(searchEngine);

  return (
    <EntitlementsProvider>
      <AtomicLayoutSection data-test="search-result-section" section="results">
        {resultState?.results && resultState.results.length > 0 && (
          <ul
            data-test="search-results-list"
            className="sm:[grid-template-columns:repeat(auto-fill,minmax(12rem,1fr))] grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(18rem,1fr))] min-[430px]:[grid-template-columns:repeat(auto-fill,minmax(12rem,1fr))] lg:[grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))] 2xl:[grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))]"
          >
            {resultState?.results.map((result) => (
              <li key={result.raw.ec_product_id as string}>
                <HeadlessProductCardWrapper
                  context={context}
                  result={result}
                  allProductTags={productTags}
                />
              </li>
            ))}
          </ul>
        )}
      </AtomicLayoutSection>
    </EntitlementsProvider>
  );
};
